import axios, { AxiosError } from "axios";

import {
  BugReportDetail,
  BugReportExportOption,
  BugReportSummary,
  DashboardSummary,
  DiscountCodeResult,
  ProductCatalogItem,
  ProductKeyResult,
  QueryResult,
  SupportReply,
  SupportSession,
  TicketDetail,
  TicketState,
  TicketSummary,
} from "./types";


export const supportApi = axios.create({
  baseURL: "/api/support-admin",
  withCredentials: true,
  headers: { "Cache-Control": "no-store" },
});

let invalidSessionHandler: (() => void) | null = null;

export function setInvalidSessionHandler(handler: (() => void) | null) {
  invalidSessionHandler = handler;
}

supportApi.interceptors.response.use(
  response => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const detail = (error.response?.data as any)?.detail;
    if (status === 401 || (status === 403 && detail?.code === "admin_disabled")) {
      invalidSessionHandler?.();
    }
    return Promise.reject(error);
  }
);

function csrfHeaders(csrfToken: string) {
  return { "X-Support-CSRF": csrfToken };
}

function bugReportExportFilename(productId: string, productVersion: string): string {
  const clean = (value: string) =>
    value.replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80) || "unknown";
  return `wavemind-bug-reports-${clean(productId)}-${clean(productVersion)}.json`;
}

function saveBugReportExport(blob: Blob, productId: string, productVersion: string): void {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  const cleanup = () => {
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  try {
    link.href = objectUrl;
    link.download = bugReportExportFilename(productId, productVersion);
    link.rel = "noopener";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
  } finally {
    window.setTimeout(cleanup, 200);
  }
}

export function errorMessage(error: unknown): string {
  if (!axios.isAxiosError(error)) return "Something went wrong.";
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail.message === "string") return detail.message;
  return "The support service could not complete that request.";
}

export const api = {
  current: async () => (await supportApi.get<SupportSession>("/auth/current")).data,
  login: async (email: string, password: string) =>
    (await supportApi.post<SupportSession>("/auth/login", { email, password })).data,
  logout: async (csrfToken: string) =>
    (await supportApi.post("/auth/logout", {}, { headers: csrfHeaders(csrfToken) })).data,
  reauthenticate: async (password: string, csrfToken: string) =>
    (
      await supportApi.post<SupportSession>(
        "/auth/reauthenticate",
        { password },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  summary: async () => (await supportApi.get<DashboardSummary>("/dashboard/summary")).data,
  queryTickets: async (
    csrfToken: string,
    state: TicketState,
    search: string,
    page: number,
    pageSize: number
  ) =>
    (
      await supportApi.post<QueryResult<TicketSummary>>(
        "/tickets/query",
        { state, search, page, page_size: pageSize },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  queryBugReports: async (
    csrfToken: string,
    state: TicketState,
    search: string,
    page: number,
    pageSize: number
  ) =>
    (
      await supportApi.post<QueryResult<BugReportSummary>>(
        "/bug-reports/query",
        { state, search, page, page_size: pageSize },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  bugReportExportOptions: async () =>
    (await supportApi.get<BugReportExportOption[]>("/bug-reports/export-options")).data,
  exportBugReports: async (
    productId: string,
    productVersion: string,
    csrfToken: string
  ): Promise<void> => {
    const response = await supportApi.post<Blob>(
      "/bug-reports/export",
      { product_id: productId, product_version: productVersion },
      { headers: csrfHeaders(csrfToken), responseType: "blob" }
    );
    saveBugReportExport(response.data, productId, productVersion);
  },
  ticket: async (id: string) => (await supportApi.get<TicketDetail>(`/tickets/${id}`)).data,
  bugReport: async (id: string) =>
    (await supportApi.get<BugReportDetail>(`/bug-reports/${id}`)).data,
  setTicketStatus: async (
    id: string,
    status: "open" | "closed",
    expectedUpdatedAt: string,
    csrfToken: string
  ) =>
    (
      await supportApi.post<TicketDetail>(
        `/tickets/${id}/status`,
        { status, expected_updated_at: expectedUpdatedAt },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  setBugReportStatus: async (
    id: string,
    status: "open" | "closed",
    expectedUpdatedAt: string,
    csrfToken: string
  ) =>
    (
      await supportApi.post<BugReportDetail>(
        `/bug-reports/${id}/status`,
        { status, expected_updated_at: expectedUpdatedAt },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  sendReply: async (
    ticketId: string,
    responseText: string,
    closeAfterSend: boolean,
    idempotencyKey: string,
    csrfToken: string,
    previousReplyId?: string
  ) => {
    const path = previousReplyId
      ? `/tickets/${ticketId}/replies/retry`
      : `/tickets/${ticketId}/replies`;
    const body: any = {
      response_text: responseText,
      close_after_send: closeAfterSend,
      idempotency_key: idempotencyKey,
    };
    if (previousReplyId) body.previous_reply_id = previousReplyId;
    return (
      await supportApi.post<SupportReply>(path, body, { headers: csrfHeaders(csrfToken) })
    ).data;
  },
  sendBugReportReply: async (
    reportId: string,
    responseText: string,
    closeAfterSend: boolean,
    idempotencyKey: string,
    csrfToken: string,
    previousReplyId?: string
  ) => {
    const path = previousReplyId
      ? `/bug-reports/${reportId}/replies/retry`
      : `/bug-reports/${reportId}/replies`;
    const body: any = {
      response_text: responseText,
      close_after_send: closeAfterSend,
      idempotency_key: idempotencyKey,
    };
    if (previousReplyId) body.previous_reply_id = previousReplyId;
    return (
      await supportApi.post<SupportReply>(path, body, { headers: csrfHeaders(csrfToken) })
    ).data;
  },
  catalog: async () =>
    (await supportApi.get<ProductCatalogItem[]>("/products/catalog")).data,
  createProductKey: async (
    productId: string,
    maxLicenses: number,
    idempotencyKey: string,
    csrfToken: string
  ) =>
    (
      await supportApi.post<ProductKeyResult>(
        "/product-keys",
        {
          product_id: productId,
          max_licenses: maxLicenses,
          idempotency_key: idempotencyKey,
        },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  createDiscountCode: async (
    name: string,
    customCode: string | undefined,
    percentage: number,
    maxRedemptions: number,
    expiresAt: string,
    idempotencyKey: string,
    csrfToken: string
  ) =>
    (
      await supportApi.post<DiscountCodeResult>(
        "/discount-codes",
        {
          name,
          custom_code: customCode,
          percentage,
          max_redemptions: maxRedemptions,
          expires_at: expiresAt,
          idempotency_key: idempotencyKey,
        },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  discountCodes: async (csrfToken: string) =>
    (
      await supportApi.get<DiscountCodeResult[]>("/discount-codes", {
        headers: csrfHeaders(csrfToken),
      })
    ).data,
  disableDiscountCode: async (
    promotionCodeId: string,
    idempotencyKey: string,
    csrfToken: string
  ) =>
    (
      await supportApi.post<DiscountCodeResult>(
        `/discount-codes/${promotionCodeId}/disable`,
        { idempotency_key: idempotencyKey },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
  deleteDiscountCode: async (
    promotionCodeId: string,
    idempotencyKey: string,
    csrfToken: string
  ) =>
    (
      await supportApi.post<DiscountCodeResult>(
        `/discount-codes/${promotionCodeId}/delete`,
        { idempotency_key: idempotencyKey },
        { headers: csrfHeaders(csrfToken) }
      )
    ).data,
};
